import { useState } from "react";
import React from "react";
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaCheckCircle, FaTimes } from 'react-icons/fa';
import './contact.css';

const initialState = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

export const Contact = (props) => {
  const [formData, setState] = useState(initialState);
  const { name, email, subject, message } = formData;
  const [feedbackMessage, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [messageType, setMessageType] = useState(""); // 'success' or 'error'
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({ ...prevState, [name]: value }));
  };

  const clearState = () => setState({ ...initialState });

  const closeSuccessPopup = () => {
    setShowSuccessPopup(false);
    setMessage("");
    setMessageType("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");
    setMessageType("");

    try {
      // Always use the full backend URL for production
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.terrytechnologie.com';
      const fullUrl = `${backendUrl}/api/contact`;

      console.log('🔍 Full URL:', fullUrl); // Debug log

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        credentials: 'include', // Match backend credentials: true
      });

      // Check if response is ok first
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setMessage(data.message || "Your message has been sent successfully!");
        setMessageType("success");
        setShowSuccessPopup(true);
        clearState();
      } else {
        // Handle validation errors
        if (data.errors && Array.isArray(data.errors)) {
          setMessage(data.errors.join('. '));
        } else {
          setMessage(data.message || "Failed to send message. Please try again.");
        }
        setMessageType("error");
      }
    } catch (error) {
      console.error('Error sending message:', error);

      // More specific error messages
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        setMessage("Unable to connect to server. Please check your internet connection or try again later.");
      } else if (error.message.includes('CORS')) {
        setMessage("Connection blocked by security policy. Please contact support.");
      } else {
        setMessage(`Error: ${error.message}`);
      }
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <section className="contact-space-background">
        <div className="contact-stars"></div>
        <div className="contact-grid-overlay"></div>

        <div className="contact-gradient-lines">
          {[...Array(5)].map((_, index) => (
              <div key={index} className={`contact-line contact-line-${index + 1}`}></div>
          ))}
        </div>

        <div className="contact-container">
          <div className="contact-card">
            <div className="contact-info">
              <div className="contact-header">
                <h1 className="contact-title">Contact Us</h1>
                <p className="contact-subtitle">
                  Have questions, suggestions, or inquiries? Reach out to our team.
                </p>
              </div>

              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">
                    <FaMapMarkerAlt />
                  </div>
                  <div className="contact-text">
                    <p className="contact-label">Address</p>
                    <p className="contact-value">{props.data?.address || "Acacia Mall, Kampala"}</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <FaPhoneAlt />
                  </div>
                  <div className="contact-text">
                    <p className="contact-label">Call Us</p>
                    <p className="contact-value">{props.data?.phone || "+256 766427210"}</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <FaEnvelope />
                  </div>
                  <div className="contact-text">
                    <p className="contact-label">Email Us</p>
                    <p className="contact-value">{props.data?.email || "info@terrytechnologie.com"}</p>
                  </div>
                </div>
              </div>

              <div className="contact-social-links">
                <div className="contact-social-title">Follow us:</div>
                <div className="contact-social-icons">
                  <button className="contact-social-icon"><FaFacebookF /></button>
                  <button className="contact-social-icon"><FaTwitter /></button>
                  <button className="contact-social-icon"><FaInstagram /></button>
                  <button className="contact-social-icon"><FaLinkedinIn /></button>
                </div>
              </div>
            </div>

            <div className="contact-form">
              <form name="sentMessage" onSubmit={handleSubmit}>
                <div className="contact-form-group">
                  <label htmlFor="name">
                    Name <span className="contact-required">*</span>
                  </label>
                  <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={name}
                      onChange={handleChange}
                      disabled={isLoading}
                  />
                </div>

                <div className="contact-form-group">
                  <label htmlFor="email">
                    Email <span className="contact-required">*</span>
                  </label>
                  <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={email}
                      onChange={handleChange}
                      disabled={isLoading}
                  />
                </div>

                <div className="contact-form-group">
                  <label htmlFor="subject">
                    Subject <span className="contact-required">*</span>
                  </label>
                  <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      value={subject}
                      onChange={handleChange}
                      disabled={isLoading}
                  />
                </div>

                <div className="contact-form-group">
                  <label htmlFor="message">
                    Message <span className="contact-required">*</span>
                  </label>
                  <textarea
                      id="message"
                      name="message"
                      required
                      value={message}
                      onChange={handleChange}
                      rows="4"
                      disabled={isLoading}
                  ></textarea>
                </div>

                <button
                    type="submit"
                    className="contact-submit-button"
                    disabled={isLoading}
                >
                  {isLoading ? "Sending..." : "Send Message"}
                </button>

                {feedbackMessage && !showSuccessPopup && (
                    <p className={`feedback-message ${messageType === 'success' ? 'success' : 'error'}`}>
                      {feedbackMessage}
                    </p>
                )}
              </form>
            </div>
          </div>

          <div className="contact-map-container">
            <h2>Our Location</h2>
            <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15955.27304655654!2d32.582519172470054!3d0.3156036738408775!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x177dbb4d7a6f37f7%3A0x3c19c39663c19a11!2sAcacia%20Mall%2C%20Kampala%2C%20Uganda!5e0!3m2!1sen!2sus!4v1686205195962!5m2!1sen!2sus&markers=color:red%7Clabel:A%7C0.3156,32.5825"
                width="100%"
                height="450"
                allowFullScreen=""
                loading="lazy"
                title="Google Map"
            ></iframe>
          </div>
        </div>

        {/* Success Popup Modal */}
        {showSuccessPopup && (
            <div className="success-popup-overlay">
              <div className="success-popup-modal">
                <div className="success-popup-content">
                  <div className="success-popup-icon">
                    <FaCheckCircle />
                  </div>
                  <h3 className="success-popup-title">Message Sent Successfully!</h3>
                  <p className="success-popup-message">
                    Thank you for reaching out to us. We have received your message and will get back to you within 24-48 hours.
                  </p>
                  <button
                      className="success-popup-close-btn"
                      onClick={closeSuccessPopup}
                  >
                    <FaTimes />
                  </button>
                  <button
                      className="success-popup-ok-btn"
                      onClick={closeSuccessPopup}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
        )}
      </section>
  );
};

export default Contact;